/**
 * The organisation's e-mail sender settings (T1a backend, T1b page): one read,
 * one write, one history read — all three `require_roles("admin")`.
 *
 * A SINGLETON, ADDRESSED BY NOTHING. The organisation comes from the token, so
 * no request carries an id, and the read never creates the settings row: an
 * organisation that has configured nothing gets every value `null`.
 *
 * THE WRITE SENDS EXACTLY WHAT IT IS GIVEN. `PATCH` is partial on the server
 * (`exclude_unset`): an absent key is left alone, an explicit `null` clears the
 * field. Which keys go out is the page's decision — it sends only the fields
 * that changed — so this function adds nothing, drops nothing and never fills in
 * the rest of the object. `birthday_email_enabled` in particular is refused with
 * a 422 whenever it is present, which is why nothing here ever sends a whole
 * settings object back.
 *
 * ERRORS ARE `Error` OBJECTS from `adminErrorFrom`: the server's sentence as
 * `message`, the HTTP status as `status`. A validation refusal (422) also gets
 * `fieldErrors` — `{ field, msg }` per refused field, read from FastAPI's
 * `detail[].loc` — because `adminErrorFrom` joins every message into one
 * sentence and keeps no field. The raw `msg` is passed on untranslated: the
 * wording lives in the page model, not in a client.
 *
 * THE HISTORY ENVELOPE IS RETURNED WHOLE (`items` + `total`). The endpoint takes
 * no parameters and answers its newest capped page, so nothing is sent and
 * `total` is what tells the page that a capped list is not the whole story.
 */
import { API_BASE_URL, adminErrorFrom, getApiHeaders } from "./core";

const BASE = "/api/admin/email-settings";

/** The four fields the write accepts — the only `loc` a field error may name. */
const WRITABLE_FIELDS = new Set([
  "sender_name",
  "sender_email",
  "reply_to",
  "test_recipient_email",
]);

/**
 * The refusal, with the refused fields on top of the server's sentence. Read
 * from the body `adminErrorFrom` left untouched (it reads a clone).
 */
async function emailSettingsErrorFrom(res) {
  const err = await adminErrorFrom(res);
  try {
    const detail = (await res.json())?.detail;
    if (Array.isArray(detail)) {
      const fieldErrors = [];
      detail.forEach((item) => {
        const loc = Array.isArray(item?.loc) ? item.loc : [];
        if (loc[0] === "body" && WRITABLE_FIELDS.has(loc[1])) {
          fieldErrors.push({
            field: loc[1],
            msg: typeof item?.msg === "string" ? item.msg : "",
          });
        }
      });
      err.fieldErrors = fieldErrors;
    }
  } catch (_) {
    // No JSON body: the sentence and the status are all there is.
  }
  return err;
}

/** This organisation's sender settings. Never creates anything. */
export async function fetchAdminEmailSettings() {
  const res = await fetch(`${API_BASE_URL}${BASE}`, {
    headers: getApiHeaders(),
  });
  if (!res.ok) throw await adminErrorFrom(res);
  return res.json();
}

/**
 * Change some of the settings. `patch` holds only the keys to change; a `null`
 * value clears that field. Answers the whole stored settings object.
 */
export async function updateAdminEmailSettings(patch) {
  const res = await fetch(`${API_BASE_URL}${BASE}`, {
    method: "PATCH",
    headers: getApiHeaders(),
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw await emailSettingsErrorFrom(res);
  return res.json();
}

/** Who changed the settings, what and when — newest first, `{ items, total }`. */
export async function fetchAdminEmailSettingsActivity() {
  const res = await fetch(`${API_BASE_URL}${BASE}/activity`, {
    headers: getApiHeaders(),
  });
  if (!res.ok) throw await adminErrorFrom(res);
  return res.json();
}
